import { Box, Button } from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FileCopy as CopyIcon,
  MoveToInbox as PasteIcon,
} from '@material-ui/icons';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useRef } from 'react';
import * as Yup from 'yup';
import {
  copyGridToClipboard,
  pasteGridValueFromClipboard,
} from '../../../../utils/agGridUtil';
import { v4 as uuidv4 } from 'uuid';
import { ISampleDevelopmentWlYarn } from '../../../../models/sampleDevelopmentWl.model';

interface ISampleDevelopmentWlYarnRow extends ISampleDevelopmentWlYarn {
  rowIndex?: string;
}

const SampleDevelopmentWlYarnRowValidationSchema = Yup.object().shape({
  sequence: Yup.number().required('Sequence is required'),
  color: Yup.string().required('Color is required'),
  pickQty: Yup.number().required('Pick Qty is required'),
});

export const ISampleDevelopmentWlYarnKeys: Array<keyof ISampleDevelopmentWlYarn> = [
  'color',
  'sequence',
  'pickQty',
];

interface SampleDevelopmentWlYarnTableProps {
  onAdd: (newRowValue: ISampleDevelopmentWlYarnRow) => void;
  onChange: (index: number, newRowValue: ISampleDevelopmentWlYarnRow) => void;
  onDelete: (selectedRowIndex: number[]) => void;
  onPaste: (newRowValues: ISampleDevelopmentWlYarnRow[]) => void;
  value: ISampleDevelopmentWlYarnRow[];
}

const cellStyle = (params: any) => {
  const { colDef, data } = params;
  if (colDef.field) {
    try {
      SampleDevelopmentWlYarnRowValidationSchema.validateSyncAt(colDef.field, data);
    } catch (e) {
      return { border: '1px solid red' };
    }
  }
  return { border: 'none' };
};

const columnDefs = [
  { headerName: '', checkboxSelection: true, width: 50 },
  {
    headerName: 'No',
    valueGetter: 'node.rowIndex + 1',
    editable: false,
  },
  {
    headerName: 'Color(สี)',
    sortable: true,
    filter: true,
    editable: true,
    field: 'color',
    cellStyle,
  },
  {
    headerName: 'Pick Qty(จำนวน Pick)',
    sortable: true,
    filter: true,
    editable: true,
    field: 'pickQty',
    cellStyle,
  },
];

const defaultNewRowValue = {
  color: '',
  pickQty: 0,
};

const SampleDevelopmentWlYarnTable: React.FC<
  SampleDevelopmentWlYarnTableProps
> = props => {
  const { onChange, value, onDelete, onPaste, onAdd } = props;
  const gridApi = useRef<GridApi>();

  const handleAdd = () => {
    onAdd({ ...defaultNewRowValue, rowIndex: uuidv4() });
  };

  const handleDelete = () => {
    const selectedNodes = gridApi.current!.getSelectedNodes();
    const selectedRowIndex = selectedNodes.map(node => node.rowIndex);
    onDelete(selectedRowIndex);
  };

  const handleCopy = () => {
    copyGridToClipboard(gridApi.current!, ISampleDevelopmentWlYarnKeys);
  };

  const handlePaste = async () => {
    await pasteGridValueFromClipboard(
      gridApi.current!,
      ISampleDevelopmentWlYarnKeys,
      defaultNewRowValue,
    );
    const rowData: any[] = [];
    gridApi.current!.forEachNode(node => {
      rowData.push(node.data);
    });
    onPaste(rowData);
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.current = params.api;
    gridApi.current.setRowData(value);
    params.api.sizeColumnsToFit();
  };

  useEffect(() => {
    if (gridApi.current !== undefined) {
      gridApi.current.setRowData(value);
    }
  }, [value]);

  return (
    <>
      <Box m={1}>
        <Button
          onClick={handleCopy}
          variant="contained"
          size="small"
          startIcon={<CopyIcon />}
        >
          Copy
        </Button>
        <Button
          onClick={handlePaste}
          variant="contained"
          size="small"
          startIcon={<PasteIcon />}
        >
          Paste
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
        >
          Add
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="secondary"
          size="small"
          startIcon={<DeleteIcon />}
        >
          Delete
        </Button>
      </Box>
      <div className="ag-theme-balham" style={{ height: '100%' }}>
        <AgGridReact
          onGridReady={onGridReady}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          rowSelection="multiple"
          singleClickEdit
          suppressRowClickSelection
          stopEditingWhenGridLosesFocus
          onCellValueChanged={(event: any) => {
            const index = event.rowIndex;
            const newRowValue = {
              sequence: event.data.sequence,
              color: event.data.color,
              pickQty: event.data.pickQty,
            };
            onChange(index, newRowValue);
          }}
        />
      </div>
    </>
  );
};

export default SampleDevelopmentWlYarnTable;
